import {
  CUSTOM_COMPANIES_PATH,
  FAVORITES_PATH,
  FUNDS_AND_PORTFOLIOS_PATH,
  MANAGE_ACCESS_PATH,
  SCREEN_COMPANIES_PATH,
  SCREEN_FUND_HOLDINGS_PATH,
  SCREEN_FUNDS_PATH,
} from '@upright/ui/common/generic/components/navigation/page-paths';
import { ImpactLensType } from '@upright/common/impact-lenses/types';
import { SSO_ERROR_PAGE_PATH } from '@upright/common/iam/login';
import { matchPath } from 'react-router-dom';
import { ALL_CHATS_PATH, isChatPath } from './unified-shell-chat-routes';

/** Only the library profile owns this frame; editing, drilldowns and corporate dashboards keep theirs. */
export function isCompanyProfilePath(pathname: string): boolean {
  const match = matchPath('/company/:id/*', pathname);
  if (!match) return false;
  const parts = (match.params['*'] ?? '').split('/').filter(Boolean);
  const subviews = ['dma', 'financial-effects', 'climate-risk'];
  const otherViews = ['internal', 'drilldown', 'dashboard'];
  if (otherViews.includes(parts[0]) || otherViews.includes(parts[1])) return false;
  return parts.length <= 1 || subviews.includes(parts[0]) || subviews.includes(parts[1]);
}

/** A product's profile is its one view; its drilldowns and internal views keep their own frame. */
export function isProductProfilePath(pathname: string): boolean {
  const match = matchPath('/product/:id/*', pathname);
  if (!match) return false;
  const parts = (match.params['*'] ?? '').split('/').filter(Boolean);
  if (['internal', 'drilldown'].some(view => parts[0] === view || parts[1] === view)) return false;
  return parts.length <= 1 || (parts.length === 2 && parts[1] === 'main');
}

/** Funds and portfolios have distinct renderers even when their display name is a company. */
export function isUnifiedProfilePath(pathname: string): boolean {
  if (isCompanyProfilePath(pathname) || isProductProfilePath(pathname)) return true;
  const match = matchPath('/company-group/:id/*', pathname) ?? matchPath('/portfolio/:id/*', pathname);
  if (!match) return false;
  const parts = (match.params['*'] ?? '').split('/').filter(Boolean);
  if (['internal', 'drilldown', 'dashboard'].some(view => parts[0] === view || parts[1] === view)) return false;
  // The portfolio router renders its profile for financial effects and climate risk too; the fund
  // router has no such routes.
  const views = pathname.startsWith('/portfolio/')
    ? ['main', 'dma', 'financial-effects', 'climate-risk']
    : ['main', 'dma'];
  return parts.length <= 1 || views.includes(parts[0]) || views.includes(parts[1]);
}

/**
 * A company, fund or portfolio impact drilldown. The profile links to it, so in the unified shell it
 * renders in the shell's frame as the profile does. A product's drilldown is not one of these: it
 * keeps the legacy frame with the rest of the product pages outside the product profile.
 */
export function isUnifiedDrilldownPath(pathname: string): boolean {
  return ['company', 'company-group', 'portfolio'].some(
    type =>
      !!(matchPath(`/${type}/:id/drilldown/*`, pathname) ?? matchPath(`/${type}/:id/:name/drilldown/*`, pathname)),
  );
}

/**
 * Every entity route that brings the unified shell's frame, and with it the top bar, itself. The
 * header switch renders no bar of its own on these, so each state such a route can be in (loading,
 * error, content) has to bring the frame.
 */
export function isUnifiedEntityFramePath(pathname: string): boolean {
  return isUnifiedProfilePath(pathname) || isUnifiedDrilldownPath(pathname);
}

const isNumericId = (id: string | undefined) => /^\d+$/.test(id ?? '');

/**
 * The internal access pages are entity pages in this frame too: an organisation's inspection,
 * and a person's and an entity's access. Only a well-formed address: anything
 * else is the page's own error under the legacy frame.
 */
export function isAccessProfilePath(pathname: string): boolean {
  const person = matchPath('/internal/access/users/:userId/*', pathname);
  if (person !== null) {
    return isNumericId(person.params.userId);
  }
  const entity = matchPath('/internal/access/entities/:type/:id/*', pathname);
  if (entity !== null) {
    return ['company', 'company-group', 'portfolio'].includes(entity.params.type ?? '');
  }
  const organization = matchPath('/internal/access/:orgId/*', pathname);
  return organization !== null && isNumericId(organization.params.orgId);
}

/** The fund or portfolio a route is about, for chrome outside its page that needs the entity. */
export function groupfolioIdFromPath(pathname: string): string | undefined {
  const match = matchPath('/company-group/:id/*', pathname) ?? matchPath('/portfolio/:id/*', pathname);
  return match?.params.id;
}

/**
 * Where a profile's Back goes, and the Back of that page in turn, so repeated Backs walk up the
 * path the user took (company → fund → portfolio → Funds and portfolios). `null` ends the path.
 */
export interface ProfileBackTarget {
  href: string;
  label: string;
  parent: ProfileBackTarget | null;
}

/**
 * Where Back goes when the profile was opened from a page of the app that cannot be named (an
 * internal tool, a legacy view), and where a shell search starts its trail. A profile with no
 * in-app origin at all has no Back (PLT-7159).
 */
export const HOME_BACK_TARGET: ProfileBackTarget = { href: '/', label: 'Home', parent: null };

/**
 * The sign-in and sign-up pages a signed-out reader passes through before reaching a deep link.
 * Eevo's hosts serve these too (`views/root/app.tsx`).
 */
export const AUTH_PATHS = ['/login', '/login-redirect', '/sign-in', '/signup', '/reset-password', SSO_ERROR_PAGE_PATH];

function isAuthPath(pathname: string): boolean {
  return AUTH_PATHS.some(path => pathname === path || pathname.startsWith(`${path}/`));
}

/** Bounds the trail kept in history state and session storage when a user clicks in circles. */
const MAX_TRAIL_DEPTH = 8;

/** The rail destinations a profile can be opened from, under the rail's own labels. */
const RAIL_PAGES: Record<string, string> = {
  '/': 'Home',
  // The company rail's *My company* (PLT-6943); its View profile link opens the company profile.
  '/company-dashboard': 'My company',
  [FUNDS_AND_PORTFOLIOS_PATH]: 'Funds and portfolios',
  [CUSTOM_COMPANIES_PATH]: 'Custom companies',
  [MANAGE_ACCESS_PATH]: 'Manage access',
  [SCREEN_COMPANIES_PATH]: 'Screen companies',
  [SCREEN_FUNDS_PATH]: 'Screen funds',
  [SCREEN_FUND_HOLDINGS_PATH]: 'Screen fund holdings',
  // Off the unified rail, but still routed and linked from bookmarks.
  [FAVORITES_PATH]: 'Favorites',
  [ALL_CHATS_PATH]: 'All chats',
};

/** Pages under a rail destination that share its label: the holdings screener's per-fund page. */
const RAIL_PAGE_PREFIXES = [SCREEN_FUND_HOLDINGS_PATH];

const ENTITY_TYPES = ['company', 'company-group', 'portfolio', 'product'];

/** Path segments after a profile's id that are views of it rather than its display name. */
const PROFILE_VIEW_SEGMENTS = [
  'main',
  'dma',
  'financial-effects',
  'climate-risk',
  'internal',
  'drilldown',
  'dashboard',
];

/**
 * The entity a page belongs to: its profile and everything under it (lenses, drilldowns, editors).
 * Moving between those keeps one Back, so a fund's drilldown never becomes the fund's parent.
 */
export function profileEntityKey(pathname: string): string | undefined {
  const match = matchPath('/:type/:id/*', pathname);
  if (!match?.params.type || !match.params.id || !ENTITY_TYPES.includes(match.params.type)) return undefined;
  return `${match.params.type}/${match.params.id}`;
}

/** The entity's display name from its URL, or the kind of entity when the URL carries none. */
function entityLabel(pathname: string): string {
  const name = matchPath('/:type/:id/:name/*', pathname)?.params.name;
  if (name && !PROFILE_VIEW_SEGMENTS.includes(name)) {
    try {
      return decodeURIComponent(name);
    } catch {
      // Already decoded by the router and holding a literal `%`.
      return name;
    }
  }
  if (pathname.startsWith('/product/')) return 'Product';
  return pathname.startsWith('/company/') ? 'Company' : pathname.startsWith('/portfolio/') ? 'Portfolio' : 'Fund';
}

/**
 * The page at a URL as a Back target: a unified profile or its impact drilldown under the entity's
 * display name, or a rail destination under its rail label. Anything else cannot be returned to
 * by name.
 */
export function profileBackTargetFor(
  pathname: string,
  search: string,
  hash = '',
): Omit<ProfileBackTarget, 'parent'> | undefined {
  const href = pathname + search + hash;
  const railPath = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const railPrefix = RAIL_PAGE_PREFIXES.find(prefix => railPath.startsWith(`${prefix}/`));
  const railLabel = RAIL_PAGES[railPrefix ?? railPath] ?? (isChatPath(railPath) ? 'Chat' : undefined);
  if (railLabel) return { href, label: railLabel };
  const isDrilldown =
    !!profileEntityKey(pathname) &&
    !!(matchPath('/:type/:id/:name/drilldown/*', pathname) ?? matchPath('/:type/:id/drilldown/*', pathname));
  if (isDrilldown) return { href, label: `${entityLabel(pathname)} drilldown` };
  if (!isUnifiedProfilePath(pathname)) return undefined;
  return { href, label: entityLabel(pathname) };
}

/**
 * Back on an impact drilldown: up to the entity's profile, not to where the entity was opened from.
 * The drilldown is a page of the profile, and the drilldown controls carry no Back of their own in the
 * unified shell, so this is the one way up. The profile is handed the entity's own trail as its
 * parent, so its Back still returns to where the entity was opened from. The selected organisation
 * rides along; the drilldown's own query (mode, filters) does not apply to the profile.
 *
 * It is labelled with the view it lands on, Net impact (every impact drilldown is one of net
 * impact), not the entity's name: the band right next to it already names the entity, and "Back to
 * Tesla" under the title "Tesla" reads as going nowhere.
 */
export function drilldownProfileBackTarget(
  pathname: string,
  search: string,
  entityTrail: ProfileBackTarget | null,
): ProfileBackTarget | undefined {
  if (!isUnifiedDrilldownPath(pathname)) return undefined;
  // Names are URL-encoded in the path, so the first `/drilldown` segment is the drilldown's.
  const profilePath = pathname.slice(0, pathname.search(/\/drilldown(?:\/|$)/));
  const profileParams = new URLSearchParams({ lens: ImpactLensType.UPRIGHT_NET_IMPACT });
  const organizationId = new URLSearchParams(search).get('organizationId');
  if (organizationId) profileParams.set('organizationId', organizationId);
  const target = profileBackTargetFor(profilePath, `?${profileParams.toString()}`);
  return target && { ...target, label: 'Net impact', parent: entityTrail };
}

/**
 * Reads a trail from history state. Only same-origin paths that are themselves Back targets are
 * accepted, and labels are re-derived from the path, so state written by anything else cannot turn
 * Back into a link off the platform or relabel it.
 */
export function readProfileBackTarget(value: unknown, depth = 0): ProfileBackTarget | null {
  if (depth >= MAX_TRAIL_DEPTH || !value || typeof value !== 'object') return null;
  if (!('href' in value) || typeof value.href !== 'string') return null;
  if (!value.href.startsWith('/') || value.href.startsWith('//') || value.href.includes('\\')) return null;
  const url = new URL(value.href, 'https://upright.invalid');
  if (url.origin !== 'https://upright.invalid') return null;
  const target = profileBackTargetFor(url.pathname, url.search, url.hash);
  if (!target) return null;
  return { ...target, parent: readProfileBackTarget('parent' in value ? value.parent : null, depth + 1) };
}

/** The history-state key a navigation sets to decide its profile's Back itself. */
export const PROFILE_BACK_STATE_KEY = 'profileBack';

/**
 * An explicit Back decided by the navigation: `null` to start a fresh trail (shell search), a
 * target when Back itself navigates and hands the page it lands on its own parent. `undefined`
 * when the state says nothing, which is every ordinary link.
 */
export function readProfileBackState(state: unknown): ProfileBackTarget | null | undefined {
  if (!state || typeof state !== 'object' || !(PROFILE_BACK_STATE_KEY in state)) return undefined;
  return readProfileBackTarget((state as Record<string, unknown>)[PROFILE_BACK_STATE_KEY]);
}

interface TrailLocation {
  key: string;
  pathname: string;
  search: string;
  hash: string;
  state: unknown;
}

/**
 * What to record as Back for the history entry just navigated to, given the entry navigated from.
 * `undefined` records nothing, and the profile then shows no Back unless an earlier visit to the
 * entry recorded one.
 *
 * - Browser back and forward return to entries that were recorded when first visited.
 * - Moving within one entity (lens, unit, its drilldowns, a rewritten URL) keeps that entity's
 *   Back, so neither the previous lens nor the entity's own drilldown becomes the parent.
 * - Otherwise the page left is the parent, carrying its own Back along as the rest of the trail,
 *   or Home when that page cannot be named: the reader still came from inside the app.
 */
export function nextProfileBackTarget(
  from: TrailLocation,
  to: TrailLocation,
  /** react-router's `NavigationType`: `'POP'`, `'PUSH'` or `'REPLACE'`. */
  navigationType: string,
  recorded: (key: string) => ProfileBackTarget | null | undefined,
): ProfileBackTarget | null | undefined {
  // Every page of an entity carries the trail, not only its profile: a company opened from a
  // fund's drilldown returns to the drilldown, and the drilldown still knows the fund's own Back.
  if (navigationType === 'POP' || !profileEntityKey(to.pathname)) return undefined;
  const explicit = readProfileBackState(to.state);
  if (explicit !== undefined) return explicit;
  const fromEntity = profileEntityKey(from.pathname);
  if (navigationType === 'REPLACE' || (fromEntity && fromEntity === profileEntityKey(to.pathname))) {
    return recorded(from.key);
  }
  // A direct link opened while signed out passes through sign-in on its way to the profile, and that
  // is still direct entry, not a page the reader came from.
  if (isAuthPath(from.pathname)) return undefined;
  const target = profileBackTargetFor(from.pathname, from.search, from.hash);
  if (!target) return HOME_BACK_TARGET;
  return readProfileBackTarget({ ...target, parent: recorded(from.key) ?? null });
}
